/**
 * usePreference — per-user DB-backed preference with localStorage cache.
 *
 * Same dual-layer pattern as usePersistedSetting:
 *  1. Initial render: read from localStorage (zero latency)
 *  2. Mount: hydrate from API (overwrites local if server differs)
 *  3. Set: write localStorage immediately, debounce API PATCH ~500ms
 *
 * Backed by the user_preferences table (JSONB blob per user per account).
 * Unlike usePersistedSetting (which maps to account_settings columns),
 * this stores arbitrary keys in a single JSONB preferences field.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

interface PreferencesResponse {
  preferences: Record<string, unknown>;
  updated_at: string | null;
}

const LS_PREFIX = 'wp.pref.';
const DEBOUNCE_MS = 500;

function readLocal<T>(key: string, def: T): T {
  if (typeof window === 'undefined') return def;
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + key);
    if (raw == null) return def;
    return JSON.parse(raw) as T;
  } catch {
    return def;
  }
}

function writeLocal<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch { /* quota exceeded */ }
}

export function usePreference<T>(
  key: string,
  defaultValue: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readLocal(key, defaultValue));
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => api<PreferencesResponse>('/v1/preferences'),
    staleTime: 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (!data?.preferences) return;
    const serverValue = data.preferences[key];
    if (serverValue === null || serverValue === undefined) return;
    setValue((curr) => {
      if (JSON.stringify(curr) === JSON.stringify(serverValue)) return curr;
      writeLocal(key, serverValue);
      return serverValue as T;
    });
  }, [data?.preferences, key]);

  const timerRef = useRef<number | null>(null);
  const pushToServer = useCallback((next: T) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(async () => {
      try {
        await api('/v1/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ [key]: next }),
        });
        void qc.invalidateQueries({ queryKey: ['user-preferences'] });
      } catch { /* silent — localStorage is the fallback */ }
    }, DEBOUNCE_MS) as unknown as number;
  }, [key, qc]);

  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v;
      writeLocal(key, next);
      pushToServer(next);
      return next;
    });
  }, [key, pushToServer]);

  return [value, set];
}

/** Read a preference synchronously from localStorage cache (no React). */
export function readPreference<T>(key: string, def: T): T {
  return readLocal(key, def);
}
